import { ChangeDetectorRef, Component, DestroyRef, Inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    CONCURRENT_USAGE_ERROR_CODES,
    ConfigConstants,
    ConfigService,
    CONNECTION_ERROR_CODES,
    ConnectivityService,
    DateModel,
    DateModelOptions,
    EventService,
    LoadingScreenComponent,
    LoadingScreenModel,
    ModalClass,
    NotificationService,
    TimesheetAndExpensesP58Model,
    TimesheetAndExpensesService,
    UserMasterService,
    WindowService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { from, of, Subject, switchMap, throwError, timer } from 'rxjs';
import { catchError, finalize, mergeMap, take, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faCheckCircle, faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons';

const CLEAR_STATUS_INTERVAL = 3000;

@Component({
    selector: 'pace-timesheet-and-expenses',
    templateUrl: './timesheet-and-expenses.component.html',
    styleUrls: ['./timesheet-and-expenses.component.scss'],
})
export class TimesheetAndExpensesComponent implements OnInit {
    public model: TimesheetAndExpensesP58Model;

    public timesheetAndExpensesForm: FormGroup;
    public timesheetFormChildren: FormArray;
    public expensesFormChildren: FormArray;

    public timesheetDatePicker: DateModel;
    public isSubmitting = false;
    private enableTimesheetEmail = false;
    private warningWorkHoursExceededAcknowledged = false;
    private warningEowSetInFutureAcknowledged = false;
    private lastCheckedEowDateTime?: Moment;
    private readonly window: Window;
    public isOnline$ = this.connectivityService.isOnline$;

    public strings: {
        timesheetHeading: string;
        expensesHeading: string;
        invalidTimesheetMessage: string;
        submittedTimesheetMessage: string;
        noSubmitOnCurrentDayMessage: string;
        downloadButtonLabel: string;
        uploadButtonLabel: string;
    };

    protected readonly faLoading = faSync;
    protected readonly faCheckCircle = faCheckCircle;
    protected readonly faExclamationTriangle = faExclamationTriangle;

    public timesheetSyncStatus = ConfigConstants.SYNCING_STATUS;

    public uploadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.timesheetSyncStatus.IDLE);
    public downloadStatus: WritableSignal<ConfigConstants.SYNCING_STATUS> = signal(this.timesheetSyncStatus.IDLE);

    private resetStatusChangeSubject: Subject<void> = new Subject<void>();

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        private readonly formBuilder: FormBuilder,
        private readonly notificationService: NotificationService,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        private readonly appNotificationService: AppNotificationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly configService: ConfigService,
        private readonly windowService: WindowService,
        private readonly userMasterService: UserMasterService,
        private readonly eventService: EventService,
        private readonly cd: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
        private readonly connectivityService: ConnectivityService,
    ) {
        this.strings = {
            timesheetHeading: <string>translate.instant('TIMESHEET_SHEET_TAB_TITLE'),
            expensesHeading: <string>translate.instant('TIMESHEET_EXPENSES_TAB_TITLE'),
            invalidTimesheetMessage: <string>translate.instant('TIMESHEET_INVALID_MESSAGE'),
            submittedTimesheetMessage: <string>translate.instant('TIMESHEET_SUBMITTED_MESSAGE'),
            noSubmitOnCurrentDayMessage: <string>translate.instant('TIMESHEET_NO_SUBMIT_ON_CURRENT_DAY_MESSAGE'),
            downloadButtonLabel: <string>translate.instant('TIMESHEET_DOWNLOAD_TIMESHEET_TITLE'),
            uploadButtonLabel: <string>translate.instant('TIMESHEET_UPLOAD_TIMESHEET_TITLE'),
        };

        this.window = windowService.nativeWindow;
    }

    public ngOnInit(): void {
        // The timesheet model is recreated automatically every time the timesheet component is displayed to always show the
        // latest time recording entries.
        this.setTimesheetData();
        this.downloadTimesheetFromConcurrencyStore();

        this.eventService.triggerTimesheetDataSync$
            .pipe(
                tap(() => {
                    this.setTimesheetData();
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();

        this.enableTimesheetEmail = this.configService.getEnableTimesheetEmail();
        const now = moment();
        const timesheetDate = <moment.Moment>this.activatedRoute.snapshot.data.date;
        this.timesheetDatePicker = new DateModel(
            'timesheet',
            timesheetDate ? timesheetDate.clone() : now.clone(),
            new DateModelOptions({ maxValue: now.clone() }),
        );

        this.timesheetFormChildren = this.formBuilder.array([]);
        this.expensesFormChildren = this.formBuilder.array([]);
        this.timesheetAndExpensesForm = this.formBuilder.group({
            timesheet: this.timesheetFormChildren,
            expenses: this.expensesFormChildren,
        });

        this.resetStatusChangeSubject
            .asObservable()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => timer(CLEAR_STATUS_INTERVAL).pipe(take(1))),
                tap(() => {
                    this.uploadStatus.set(this.timesheetSyncStatus.IDLE);
                    this.downloadStatus.set(this.timesheetSyncStatus.IDLE);
                }),
            )
            .subscribe();
    }

    public setTimesheetData(): void {
        if (this.clientManager.isNgmP58()) {
            this.model = new TimesheetAndExpensesP58Model();
        } else {
            throw new Error(`Could not determine which timesheet-and-expenses model to create!`);
        }
        this.cd.markForCheck();
    }

    public isTimesheetSendDisabled(): boolean {
        return (
            this.timesheetAndExpensesForm.invalid ||
            this.model.readonly ||
            this.isSubmitting ||
            this.model.timesheetEntries.value.length === 0
        );
    }

    public isExpenseFormInvalid(): boolean {
        return this.expensesFormChildren ? this.expensesFormChildren.invalid : false;
    }

    public isTimesheetFormInvalid(): boolean {
        return this.timesheetFormChildren ? this.timesheetFormChildren.invalid : false;
    }

    public submit(): void {
        // make sure that submit can only be run once
        if (this.isSubmitting) {
            return;
        }

        // Show a warning if the end of work is in the future. If it is, show a warning popup, but don't block the user.
        // Same pattern as below -> work hours exceeds 10 hours
        const timesheetEowEntry = this.model.timesheetEntries.value.find((entry) =>
            TimesheetAndExpensesService.isEowEntry(entry),
        );

        if (
            this.warningEowSetInFutureAcknowledged &&
            timesheetEowEntry &&
            !this.lastCheckedEowDateTime?.isSame(timesheetEowEntry?.endTime.value)
        ) {
            this.warningEowSetInFutureAcknowledged = false;
        }

        this.lastCheckedEowDateTime = moment(timesheetEowEntry?.endTime.value);

        if (timesheetEowEntry && timesheetEowEntry.endTime.value.isAfter() && !this.warningEowSetInFutureAcknowledged) {
            const warningEoWModal = this.modalService.show(YesNoModalDialogComponent, {
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_TITLE'),
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_BODY'),
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_OK_ACTION'),
                        this.translate.instant('TIMESHEET_EOW_IN_FUTURE_WARNING_NO_ACTION'),
                    ),
                },
                backdrop: 'static',
                keyboard: false,
                ariaLabelledBy: 'yes-no-modal-title',
            });

            warningEoWModal.content?.modalResult
                .asObservable()
                .pipe(
                    take(1),
                    tap((resultAction) => {
                        this.warningEowSetInFutureAcknowledged = resultAction;
                    }),
                )
                .subscribe();

            return;
        }

        this.isSubmitting = true;

        // NGMP58- show warning if total work hours is more than 10hours
        if (
            this.configService.getMaxWorkingHoursInTimesheet() > -1 &&
            this.timesheetAndExpensesService.isExceedingWorkHours(this.model) &&
            !this.warningWorkHoursExceededAcknowledged
        ) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('TIMESHEET_TIME_HOURS_EXCEEDING_TITLE'),
                <string>this.translate.instant('TIMESHEET_TIME_HOURS_EXCEEDING_LABEL', {
                    workhour: this.configService.getMaxWorkingHoursInTimesheet(),
                }),
                () => {
                    this.warningWorkHoursExceededAcknowledged = true;
                },
            );
            this.isSubmitting = false;
            return;
        }
        // P58: Display error message if there are unconfirmed notifications in progress since we only allow to submit the
        //      timesheet once
        let notificationInProgress = this.notificationService.getNotificationInProgress();
        let notificationToBeSigned = this.notificationService.getNotificationToBeSigned();
        let notificationForWarning = notificationInProgress || notificationToBeSigned;
        let inProgressWarningText = '';

        // check if there are any unfinished (=in progress or to be signed) notifications
        if (this.clientManager.isNgmP58() && notificationForWarning) {
            inProgressWarningText = <string>this.translate.instant('TIMESHEET_NOTIFICATION_IN_PROGRESS_ERROR_LABEL', {
                qmnum: notificationForWarning.identifier.qmNum,
                manum: notificationForWarning.identifier.maNum,
            });
            const errorModal = this.modalService.show(YesNoModalDialogComponent, {
                class: ModalClass.MODAL_MEDIUM,
                initialState: {
                    model: new YesNoModalDialogModel(
                        <string>this.translate.instant('TIMESHEET_NOTIFICATION_IN_PROGRESS_ERROR_TITLE'),
                        inProgressWarningText,
                        <string>this.translate.instant('TIMESHEET_NOTIFICATION_IN_PROGRESS_OK_LABEL'),
                    ),
                },
                ariaLabelledBy: 'yes-no-modal-title',
            });

            errorModal.content?.modalResult.subscribe(() => {
                this.isSubmitting = false;
            });

            return;
        }

        // open progress modal while submitting
        const loadingScreenModel = new LoadingScreenModel(<string>this.translate.instant('TIMESHEET_SUBMIT_MESSAGE'));
        const submitModalInstance = this.modalService.show(LoadingScreenComponent, {
            class: 'modal-sm',
            initialState: {
                model: loadingScreenModel,
            },
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        loadingScreenModel.loadingScreenDisplayed$
            .pipe(
                tap(() => this.timesheetAndExpensesService.saveSubmitStatus(true)),
                mergeMap(() =>
                    this.timesheetAndExpensesService.isCurrentTimesheetUpToDate().pipe(
                        mergeMap((isTimesheetUpToDate) => {
                            if (!isTimesheetUpToDate) {
                                // TODO: Code cleanup. Had to return the enum value instead of an Error object here,
                                //  because it is done so for the connection error codes as well. Should be refactored
                                //  so that Error object is returned.
                                return throwError(() => CONCURRENT_USAGE_ERROR_CODES.TIMESHEET_OUTDATED);
                            }
                            return of(undefined);
                        }),
                    ),
                ),
                mergeMap(() =>
                    this.notificationService.synchronizeNotificationsCrossDevice().pipe(
                        mergeMap(() => {
                            // P58: Display error message if there are unconfirmed notifications in progress since we only allow to submit the
                            //      timesheet once
                            notificationInProgress = this.notificationService.getNotificationInProgress();
                            notificationToBeSigned = this.notificationService.getNotificationToBeSigned();
                            notificationForWarning = notificationInProgress || notificationToBeSigned;

                            // check if there are any unfinished (=in progress or to be signed) notifications
                            if (this.clientManager.isNgmP58() && notificationForWarning) {
                                inProgressWarningText = <string>this.translate.instant(
                                    'TIMESHEET_NOTIFICATION_IN_PROGRESS_ERROR_LABEL',
                                    {
                                        qmnum: notificationForWarning.identifier.qmNum,
                                        manum: notificationForWarning.identifier.maNum,
                                    },
                                );
                                return throwError(() => CONCURRENT_USAGE_ERROR_CODES.NOTIFICATIONS_OUTDATED);
                            }
                            return of(undefined);
                        }),
                    ),
                ),
                // save changed values in expenses and timesheet section
                mergeMap(() => this.timesheetAndExpensesService.saveAndUploadTimesheet(this.model)),
                // send the whole timesheet to the offline plugin
                mergeMap(() => this.timesheetAndExpensesService.submit(this.model)),
                mergeMap(() => from(this.router.navigate([ConfigConstants.routeUrlDashboard]))),
                tap(() => {
                    if (this.enableTimesheetEmail) {
                        this.generateTimesheetAndExpensesEmail();
                    }
                }),
                catchError((error) => {
                    this.log.error(
                        'Error while submitting timesheet: ' + JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    switch (error) {
                        case CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE'),
                                <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY'),
                            );
                            break;
                        case CONCURRENT_USAGE_ERROR_CODES.TIMESHEET_OUTDATED:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('TIMESHEET_REMOTE_UPDATE_REQUIRED_TITLE'),
                                <string>this.translate.instant('TIMESHEET_REMOTE_UPDATE_REQUIRED_BODY'),
                            );
                            break;
                        case CONCURRENT_USAGE_ERROR_CODES.NOTIFICATIONS_OUTDATED:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('TIMESHEET_NOTIFICATION_IN_PROGRESS_ERROR_TITLE'),
                                inProgressWarningText,
                            );
                            break;
                        default:
                            this.appNotificationService.setAppNotification(
                                AppNotificationType.ERROR,
                                <string>this.translate.instant('TIMESHEET_REQUEST_ERROR_TITLE'),
                                <string>this.translate.instant('TIMESHEET_REQUEST_ERROR_BODY'),
                            );
                            break;
                    }
                    this.isSubmitting = false;
                    return of(undefined);
                }),
                finalize(() => {
                    this.timesheetAndExpensesService.saveSubmitStatus(false);
                }),
            )
            .subscribe(() => {
                this.isSubmitting = false;
                submitModalInstance.hide();
            });
    }

    public downloadTimesheetFromConcurrencyStore(): void {
        this.downloadStatus.set(this.timesheetSyncStatus.LOADING);
        this.timesheetAndExpensesService
            .refreshTimesheets()
            .pipe(
                take(1),
                tap(() => {
                    this.setTimesheetData();
                    this.downloadStatus.set(this.timesheetSyncStatus.SUCCESS);
                }),
                catchError((error) => {
                    this.downloadStatus.set(this.timesheetSyncStatus.ERROR);
                    return throwError(() => error);
                }),
                finalize(() => this.clearStatus()),
            )
            .subscribe(() => {});
    }

    public saveAndUploadTimesheetToConcurrencyStore(): void {
        this.uploadStatus.set(this.timesheetSyncStatus.LOADING);
        this.timesheetAndExpensesService
            .saveAndUploadTimesheet(this.model)
            .pipe(
                take(1),
                tap(() => {
                    this.uploadStatus.set(this.timesheetSyncStatus.SUCCESS);
                }),
                catchError((error) => {
                    this.uploadStatus.set(this.timesheetSyncStatus.ERROR);
                    return throwError(() => error);
                }),
                finalize(() => this.clearStatus()),
            )
            .subscribe();
    }

    public triggerAutoSaveTimesheet(): void {
        this.timesheetAndExpensesService.autoSaveTimesheetForm(this.model);
    }

    private generateTimesheetAndExpensesEmail(): void {
        const curlyBraceOpenRegExp = new RegExp('{', 'g');
        const curlyBraceCloseRegExp = new RegExp('}', 'g');
        const commaRegExp = new RegExp(',', 'g');

        const emailId = this.userMasterService.getSelectedUserModel().getEmailId();

        const timesheet = this.timesheetAndExpensesService.getSubmittedTimesheet();
        const startDateString = timesheet.startOfWork.format(ConfigConstants.formatPatternDateGeneric);
        const startTimeString = timesheet.startOfWork.format(ConfigConstants.formatPatternTimeGeneric);
        const endDateString = timesheet.getEndOfWork().format(ConfigConstants.formatPatternDateGeneric);
        const endTimeString = timesheet.getEndOfWork().format(ConfigConstants.formatPatternTimeGeneric);

        const emailSubject = <string>this.translate.instant('TIMESHEET_EMAIL_SUBJECT', {
            startDate: startDateString,
            startTime: startTimeString,
            endDate: endDateString,
            endTime: endTimeString,
        });
        const emailBody = JSON.stringify(this.model.getOutModel())
            .replace(curlyBraceOpenRegExp, '{\n')
            .replace(curlyBraceCloseRegExp, '\n}')
            .replace(commaRegExp, ',\n');

        this.window.location.href =
            `mailTo:${emailId}?` + `subject=${emailSubject}` + `&body=${encodeURIComponent(emailBody)}`;
    }

    private clearStatus(): void {
        this.resetStatusChangeSubject.next();
    }
}
